import AccessDenied from '@components/AccessDenied'
import CoreLoader from '@components/CoreLoader'
import { useHeaderContext } from '@context/HeaderContext'
import CountryRestrictedPane from '@withdrawals/components/CountryRestrictedPane'
import NoSuitableAccountModal from '@withdrawals/components/NoSuitableAccountModal'
import SendMoneyNavigation from '@withdrawals/components/SendMoneyNavigation'
import { findFlowByPathname, findSupportedSubFlow, getFlowPath } from '@withdrawals/helpers'
import type { ExtendedSubFlow } from '@withdrawals/types'
import BankTransfer from '@withdrawals/views/BankTransfer'
import CryptoTransfer from '@withdrawals/views/CryptoTransfer'
import InteracTransfer from '@withdrawals/views/InteracTransfer'
import InternalTransfer from '@withdrawals/views/InternalTransfer'
import IntraAccountTransfer from '@withdrawals/views/IntraAccountTransfer'
import JetonWalletTransfer from '@withdrawals/views/JetonWalletTransfer'
import LinkedAccountsTransfer from '@withdrawals/views/LinkedAccountsTransfer'
import MastercardTransfer from '@withdrawals/views/MastercardTransfer'
import PayToCard from '@withdrawals/views/PayToCard'
import PerfectMoneyTransfer from '@withdrawals/views/PerfectMoneyTransfer'
import PixTransfer from '@withdrawals/views/PixTransfer'
import SkrillTransfer from '@withdrawals/views/SkrillTransfer'
import i18n from 'i18next'
import {
  isOwnerOrAdministrator,
  paths,
  selectCurrentUserRole,
  useAppSelector,
  useLazyFetchWithdrawAccountsQuery,
  useMerchantQry,
  useMerchantsForSwitchQry,
  useMyAccountAccessQry,
  useWithdrawSubFlowsExtended,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import { GlobalContext, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useMemo } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import styles from './SendMoney.module.scss'

const priorities: ExtendedSubFlow[] = [
  WithdrawalSubFlows.BANKWIRE,
  WithdrawalSubFlows.MASTERCARD,
  WithdrawalSubFlows.INTRA_ACCOUNT_TRANSFER,
  WithdrawalSubFlows.INTERNAL_TRANSFER,
  WithdrawalSubFlows.CRYPTO,
  WithdrawalSubFlows.BATCH,
  'SUB_ACCOUNTS',
  WithdrawalSubFlows.PAY_TO_CARD
]

const SendMoney = () => {
  const { currentStep, setCurrentStep, setTotalSteps } = useHeaderContext()
  const { modalShow } = useContext(GlobalContext)

  const sendMoneySteps = useMemo(
    () => [
      {
        step: 1,
        context: tr('FRONTEND.BANKING.SEND_MONEY.METHOD_ACCOUNT_LABEL', 'Select Method & Account'),
        subtitle: ''
      },
      {
        step: 2,
        context: tr('FRONTEND.BANKING.SEND_MONEY.REVIEW_CONFIRM_LABEL', 'Review & Confirm'),
        subtitle: ''
      }
    ],
    [i18n.language]
  )

  const customerRole = useAppSelector(selectCurrentUserRole)
  const { merchant } = useMerchantQry()

  const { withdrawSubFlowsExtended, withdrawSubFlowsExtendedError, withdrawSubFlowsExtendedLoading } =
    useWithdrawSubFlowsExtended()

  const { subAccounts, subAccountsFetching } = useMerchantsForSwitchQry()

  const [fetchAccountsRequest, { error: accountsError, isFetching: accountsFetching }] =
    useLazyFetchWithdrawAccountsQuery()

  const { accountAccessFetching, hasAnyWritePermission, hasAnyRequestPermission } = useMyAccountAccessQry()

  const history = useHistory()
  const location = useLocation()

  const paymentOptions = useMemo(() => {
    const options = withdrawSubFlowsExtended.map(entry => {
      const option = findSupportedSubFlow(entry.withdrawalSubFlow)
      return option ? { ...option, paymentMethodDescription: entry.paymentMethodDescription } : option
    })

    // Add SUB_ACCOUNTS for admin/owners if merchants available
    if (isValidArray(subAccounts) && isOwnerOrAdministrator(customerRole)) {
      options.push(findSupportedSubFlow('SUB_ACCOUNTS'))
    }

    // Sort by priority order
    const sortedArray = options.sort((a, b) => {
      const a1 = priorities.indexOf(a?.paymentMethod) !== -1 ? priorities.indexOf(a?.paymentMethod) : Infinity
      const b1 = priorities.indexOf(b?.paymentMethod) !== -1 ? priorities.indexOf(b?.paymentMethod) : Infinity
      return a1 - b1
    })

    return sortedArray?.filter(Boolean)
  }, [customerRole, subAccounts, withdrawSubFlowsExtended, i18n.language])

  // Current payment option based on URL and available options
  const currentPaymentOption = useMemo(() => {
    const method = findFlowByPathname(location?.pathname)

    if (method && isValidArray(paymentOptions)) {
      const find = paymentOptions.find(option => option?.paymentMethod === method)
      return find ?? null
    }

    return null
  }, [location?.pathname, paymentOptions])

  const showNoSuitableAccountModal = (flowType: ExtendedSubFlow) => {
    modalShow({
      header: findSupportedSubFlow(flowType)?.title,
      content: <NoSuitableAccountModal />,
      options: { size: 'medium', closeOnClickOutside: false }
    })
  }

  const handlePaymentOptionChanged = (flowType: ExtendedSubFlow, redirectUrl: string) => {
    if (flowType === WithdrawalSubFlows.BATCH) {
      history.push(redirectUrl)
      return
    }

    fetchAccountsRequest(flowType === 'SUB_ACCOUNTS' ? WithdrawalSubFlows.INTERNAL_TRANSFER : flowType)
      .unwrap()
      .then(accounts => {
        if (isValidArray(accounts)) {
          history.push(redirectUrl)
        } else {
          showNoSuitableAccountModal(flowType)
        }
      })
      .catch(() => {
        // errors are handled through RTK query
      })
  }

  useEffect(() => {
    return () => {
      setCurrentStep(1)
    }
  }, [])

  useEffect(() => {
    setTotalSteps(sendMoneySteps)
  }, [sendMoneySteps])

  // Navigate to first option if no URL match (runs once when options are ready)
  useEffect(() => {
    if (
      !withdrawSubFlowsExtendedLoading &&
      !subAccountsFetching &&
      !currentPaymentOption &&
      isValidArray(withdrawSubFlowsExtended)
    ) {
      const firstOption = paymentOptions[0]
      if (firstOption) {
        history.replace(firstOption.path)
      }
    }
  }, [withdrawSubFlowsExtended])

  return (
    <div className={styles['send-money-wrapper']}>
      <ErrorDisplay error={[accountsError, withdrawSubFlowsExtendedError]} />

      {withdrawSubFlowsExtendedLoading || subAccountsFetching || accountAccessFetching ? (
        <CoreLoader />
      ) : !hasAnyWritePermission && !hasAnyRequestPermission ? (
        <AccessDenied />
      ) : merchant?.countryRestricted && !paymentOptions?.length ? (
        <CountryRestrictedPane />
      ) : (
        <div className={styles['send-money-container']}>
          {currentStep === 1 && (
            <SendMoneyNavigation
              loading={accountsFetching}
              selectedOption={currentPaymentOption}
              setSelectedOption={handlePaymentOptionChanged}
              paymentOptions={paymentOptions}
            />
          )}

          <div className={styles['send-money-content']}>
            <Switch>
              <Route path={getFlowPath(WithdrawalSubFlows.BANKWIRE)} exact component={BankTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.CRYPTO)} exact component={CryptoTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.INTERNAL_TRANSFER)} exact component={InternalTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.JETON_WALLET)} exact component={JetonWalletTransfer} />
              <Route
                path={getFlowPath(WithdrawalSubFlows.INTRA_ACCOUNT_TRANSFER)}
                exact
                component={IntraAccountTransfer}
              />
              <Route path={getFlowPath(WithdrawalSubFlows.PIX)} exact component={PixTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.INTERAC)} exact component={InteracTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.PERFECT_MONEY)} exact component={PerfectMoneyTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.SKRILL)} exact component={SkrillTransfer} />
              <Route path={getFlowPath(WithdrawalSubFlows.PAY_TO_CARD)} exact component={PayToCard} />
              <Route path={getFlowPath(WithdrawalSubFlows.MASTERCARD)} exact component={MastercardTransfer} />
              <Route path={getFlowPath('SUB_ACCOUNTS')} exact component={LinkedAccountsTransfer} />
              <Route path={paths.banking.send()} exact />

              <Redirect to={isValidArray(paymentOptions) ? paymentOptions[0].path : paths.banking.sendBankTransfer()} />
            </Switch>
          </div>
        </div>
      )}
    </div>
  )
}

export default SendMoney
